import React, { ReactElement } from 'react';
import cn from 'clsx';
import Card from '@components-clean/core/card';
import { ExtendedRiskActionFields } from '@pages-clean/risk-assessment/risk-log/constants';
import Action from '@pages-clean/risk-assessment/risk-log/dialogs/email-actions/action';
import styles from '@pages-clean/risk-assessment/risk-log/dialogs/email-actions/email-actions-dialog.module.scss';
import { useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';

const LabelText = ({ children, inline }: React.PropsWithChildren & { inline?: boolean }): ReactElement => {
    return <span className={cn(' k-font-weight-medium', { 'k-pr-2': inline })}>{children}</span>;
};

const Observation = ({
    observation,
    obIndex,
}: {
    observation: {
        createdBy?: string;
        recordId?: number;
        observationDate?: string;
        created?: string;
        comments: string | null;
        description: string | null;
        resolution: string | null;
        closedDate?: string;
        _currentActions?: ExtendedRiskActionFields[];
    };
    obIndex: number;
}) => {
    const { t } = useTranslation('ram', { keyPrefix: 'riskLog.dialogs.emailActions' });
    const { recordId, observationDate, created, comments, description, closedDate, createdBy } = observation;
    const isFirstObservation = obIndex === 0;
    return (
        <Card
            data-testid={`email-actions-observation-${recordId}`}
            title={`${t('sectionHeadings.observation')} - ${recordId}`}
            className={cn(isFirstObservation ? 'k-mt-6' : 'k-mt-3.5', styles.cardCustomBorder)}
            padding="none"
            key={obIndex}
        >
            <div className="k-d-flex-col">
                <div
                    className={cn(
                        styles.infoBlock,
                        'k-d-flex-row k-justify-content-between k-align-items-center k-font-size-lg k-py-3 k-px-7.5',
                    )}
                >
                    <span>
                        <LabelText inline>{t('observationDateLabel')}</LabelText>
                        {stringDateWithoutTimeFromUtc(observationDate, 'DD-MMM-YYYY') || t('n/a')}
                    </span>
                    <div className="k-font-size-lg">
                        <span className="k-font-weight-medium k-pr-2">{t('created')}</span>
                        <span>{stringDateWithoutTimeFromUtc(created, 'DD-MMM-YYYY') || t('n/a')}</span>
                        <span>{t('by')}</span>
                        <span>{createdBy}</span>
                    </div>
                </div>
                <div className="k-py-3 k-px-7.5 k-font-size-md">
                    <div>
                        <div>
                            <p className="k-font-weight-medium k-font-size-lg">{t('observationLabel')}</p>
                            <p>{description || t('n/a')}</p>
                        </div>
                        <div>
                            <p className="k-font-weight-medium k-font-size-lg">{t('comments')}</p>
                            <p>{comments || t('n/a')}</p>
                        </div>
                        <span className="k-font-size-lg">
                            <LabelText inline>{t('closedDate')}</LabelText>
                            {stringDateWithoutTimeFromUtc(closedDate, 'DD-MMM-YYYY') || t('n/a')}
                        </span>
                    </div>
                    {observation._currentActions?.length
                        ? observation._currentActions.map((action) => {
                              // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
                              // eslint-disable-next-line @eslint-react/no-missing-key
                              return <Action action={action} />;
                          })
                        : null}
                </div>
            </div>
        </Card>
    );
};
export default Observation;
